import { Badge, Button, Checkbox, CopyButton, Group, Modal, Select, SimpleGrid, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import type { QueueMatch } from "../../lib/api";
import type { Job } from "../../types/api";
import { axioTokens } from "../../theme";
import { CallDialQr } from "./CallDialQr";
import {
  CALL_COPY_NUMBER_LABEL,
  CALL_NO_PHONE_COPY,
  CALL_NUMBER_COPIED,
  DISPOSITIONS,
  MATCH_SCORE_LABEL,
  REJECT_REASONS,
} from "./constants";
import { candidateMetaLine } from "./exploreUtils";
import { CallBriefingCallout, CtcBadge, ScoreBadge, SkillFitBadges } from "./MatchCard";

function CallOutcomeIdentity({ match, job }: { match: QueueMatch; job: Job | undefined }) {
  const c = match.candidate;
  if (!c) return null;
  const meta = candidateMetaLine(c);
  return (
    <Group justify="space-between" align="stretch" wrap="nowrap" gap="lg" w="100%">
      <Stack gap="sm" style={{ flex: "var(--axio-number-one)", minWidth: "var(--axio-unit-0)" }}>
        <Group gap="sm" wrap="wrap" align="baseline">
          <Text fw="var(--axio-font-weight-extra-bold)" size="lg" lh="var(--axio-line-height-compact)">
            {c.full_name}
          </Text>
          {match.callable_profile === false && (
            <Badge color="orange" variant="light">
              Needs details
            </Badge>
          )}
        </Group>
        {c.current_title ? (
          <Text size="sm" fw="var(--axio-font-weight-semibold)" lh="var(--axio-line-height-compact)">
            {c.current_title}
          </Text>
        ) : null}
        {c.phone ? (
          <Group gap="sm" wrap="wrap">
            <Text
              fw="var(--axio-font-weight-bold)"
              className="tabular"
              style={{ fontFamily: axioTokens.fontHead }}
            >
              {c.phone}
            </Text>
            <CopyButton value={c.phone}>
              {({ copied, copy }) => (
                <Button
                  size="compact-sm"
                  variant={copied ? "filled" : "default"}
                  color={copied ? "green" : undefined}
                  leftSection={copied ? <IconCheck size="var(--axio-unit-16)" /> : <IconCopy size="var(--axio-unit-16)" />}
                  onClick={copy}
                >
                  {copied ? CALL_NUMBER_COPIED : CALL_COPY_NUMBER_LABEL}
                </Button>
              )}
            </CopyButton>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            {CALL_NO_PHONE_COPY}
          </Text>
        )}
        {meta ? (
          <Text size="sm" c="dimmed">
            {meta}
          </Text>
        ) : null}
        {c.email ? (
          <Text size="sm" c="dimmed">
            {c.email}
          </Text>
        ) : null}
        <CtcBadge match={match} job={job} />
      </Stack>
      {c.phone ? <CallDialQr phone={c.phone} fill /> : null}
    </Group>
  );
}

export function CallOutcomeModals({
  dispOpen,
  releaseConfirmOpen,
  active,
  job,
  notes,
  rejectReason,
  neverCall,
  pendingDisposition,
  briefingLoading,
  briefingError,
  briefingSummary,
  onRetryBriefing,
  onNotesChange,
  onRejectReasonChange,
  onNeverCallChange,
  onDisposition,
  onPendingDisposition,
  onRequestRelease,
  onCancelRelease,
  onConfirmRelease,
  dispositionPending,
  releasePending,
}: {
  dispOpen: boolean;
  releaseConfirmOpen: boolean;
  active: QueueMatch | null;
  job: Job | undefined;
  notes: string;
  rejectReason: string | null;
  neverCall: boolean;
  pendingDisposition: string | null;
  briefingLoading: boolean;
  briefingError: boolean;
  briefingSummary: string | null | undefined;
  onRetryBriefing: () => void;
  onNotesChange: (value: string) => void;
  onRejectReasonChange: (value: string | null) => void;
  onNeverCallChange: (value: boolean) => void;
  onDisposition: (disposition: string) => void;
  onPendingDisposition: (disposition: string) => void;
  onRequestRelease: () => void;
  onCancelRelease: () => void;
  onConfirmRelease: () => void;
  dispositionPending: boolean;
  releasePending: boolean;
}) {
  return (
    <>
      <Modal
        opened={dispOpen}
        onClose={() => undefined}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        size="lg"
        padding="xl"
      >
        <Stack gap="var(--axio-unit-32)">
          {active ? <CallOutcomeIdentity match={active} job={job} /> : null}
          {active ? (
            <Stack gap="md">
              <Group gap="sm" align="baseline">
                <ScoreBadge match={active} />
                <Text size="sm" c="dimmed">
                  {MATCH_SCORE_LABEL}
                </Text>
              </Group>
              <SkillFitBadges match={active} />
            </Stack>
          ) : null}
          <CallBriefingCallout
            isLoading={briefingLoading}
            isError={briefingError}
            summary={briefingSummary}
            onRetry={onRetryBriefing}
          />
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              How did the call go?
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {DISPOSITIONS.map((d) => (
                <Button
                  key={d.value}
                  variant={pendingDisposition === d.value ? "filled" : "outline"}
                  color={d.color}
                  loading={dispositionPending && pendingDisposition === d.value}
                  disabled={dispositionPending || releasePending}
                  onClick={() => {
                    if (d.value === "not_a_fit" && !rejectReason) {
                      onPendingDisposition(d.value);
                      notifications.show({
                        color: "orange",
                        message: "Pick a reject reason below, then tap Not a fit again.",
                      });
                      return;
                    }
                    onPendingDisposition(d.value);
                    onDisposition(d.value);
                  }}
                >
                  {d.label}
                </Button>
              ))}
            </SimpleGrid>
          </Stack>
          <Stack gap="md">
            <Select
              label="Not a fit — reason"
              data={REJECT_REASONS}
              value={rejectReason}
              onChange={onRejectReasonChange}
              clearable
            />
            <Checkbox
              label="Never call again (only sent with Not interested)"
              checked={neverCall}
              onChange={(e) => onNeverCallChange(e.currentTarget.checked)}
            />
            <Textarea
              label="Notes"
              value={notes}
              onChange={(e) => onNotesChange(e.currentTarget.value)}
              minRows={2}
            />
            <Group justify="space-between">
              <Button
                variant="subtle"
                color="gray"
                onClick={onRequestRelease}
                disabled={dispositionPending || releasePending}
              >
                Release without calling
              </Button>
            </Group>
          </Stack>
        </Stack>
      </Modal>

      <Modal
        opened={releaseConfirmOpen}
        onClose={onCancelRelease}
        title="Release without calling?"
        centered

      >
        <Stack gap="md">
          <Text size="sm">
            This drops your lock and puts {active?.candidate?.full_name ?? "this candidate"} back in the shared queue.
            The queue may refresh, and you might not see them again if someone else claims them first.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={onCancelRelease}
              disabled={releasePending}
            >
              Keep lock
            </Button>
            <Button
              color="gray"
              loading={releasePending}
              onClick={onConfirmRelease}
            >
              Release
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
